import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Loader2, RefreshCw, Check, Minus, AlertTriangle } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { fetchWeeklyChecklist } from '@/pages/monitoring/services/monitoring.services';

const ESTADOS = {
  ok: {
    label: 'Al día',
    className:
      'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  },
  pendiente: {
    label: 'Con pendientes',
    className: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  },
  faltante: {
    label: 'Sin registrar',
    className: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',
  },
  futuro: {
    label: 'Aún no aplica',
    className: 'bg-muted/50 text-muted-foreground/50 border-border border-dashed',
  },
};

/** Contenido de una celda: el estado manda, el número da el contexto. */
const Celda = ({ celda, tipo }) => {
  const meta = ESTADOS[celda?.estado] || ESTADOS.futuro;

  let contenido;
  if (celda?.estado === 'futuro') {
    contenido = <span className="text-xs">·</span>;
  } else if (celda?.estado === 'faltante') {
    contenido = (
      <>
        <Minus className="h-4 w-4" strokeWidth={2.5} />
        <span className="text-[10px] font-medium">Sin registrar</span>
      </>
    );
  } else if (tipo === 'pendientes') {
    const pend = celda.pendientes;
    contenido = (
      <>
        {pend > 0 ? (
          <AlertTriangle className="h-4 w-4" strokeWidth={2.5} />
        ) : (
          <Check className="h-4 w-4" strokeWidth={2.5} />
        )}
        <span className="text-[11px] font-semibold tabular-nums">
          {pend > 0 ? `${pend} de ${celda.total}` : `${celda.total} ✓`}
        </span>
        <span className="text-[9px] opacity-70">
          {pend > 0 ? 'pendientes' : 'sin pendientes'}
        </span>
      </>
    );
  } else {
    contenido = (
      <>
        <Check className="h-4 w-4" strokeWidth={2.5} />
        <span className="text-[11px] font-semibold tabular-nums">{celda.total}</span>
      </>
    );
  }

  return (
    <div
      className={`flex min-h-[52px] flex-col items-center justify-center gap-0.5 rounded-md border px-1.5 py-1 ${meta.className}`}
      title={meta.label}
    >
      {contenido}
    </div>
  );
};

/**
 * Lista de chequeo semanal del supervisor: filas = tareas, columnas = días.
 *
 * Verifica que la app se esté llenando correctamente en el centro durante la
 * semana. Se apoya en POST /monitoring/weekly-checklist, que agrega los
 * registros crudos (turnos, servicios públicos, recepciones y marcaciones).
 */
const WeeklyChecklistCard = ({ laundryId, weekStart }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!laundryId || !weekStart) return;
    setLoading(true);
    try {
      const resp = await fetchWeeklyChecklist({ laundryId, weekStart });
      if (!resp?.state) throw new Error(resp?.message || 'Respuesta inválida');
      setData(resp.data);
    } catch (e) {
      toast({
        title: 'Error',
        description: e.message || 'No se pudo cargar la lista de chequeo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [laundryId, weekStart, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const days = data?.days || [];
  const tareas = data?.tareas || [];
  const pct = data?.resumen?.porcentaje ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-4 border-b border-border/50 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-sky-500/15 p-2.5">
            <ClipboardList className="h-6 w-6 text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <CardTitle className="text-lg">Lista de chequeo semanal</CardTitle>
            <CardDescription>
              Verificación de que la app se esté llenando correctamente día a día.
            </CardDescription>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data && (
            <Badge
              variant={pct === 100 ? 'success' : pct >= 70 ? 'warning' : 'destructive'}
              className="tabular-nums"
            >
              {data.resumen.cumplidos}/{data.resumen.exigibles} al día ·{' '}
              {pct.toFixed(0)}%
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        {loading && !data ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-3 text-sm text-muted-foreground">
              Revisando la semana…
            </span>
          </div>
        ) : tareas.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Sin datos para la semana seleccionada.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-x-1 border-spacing-y-1 text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 min-w-[220px] bg-card px-2 pb-2 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Tarea
                    </th>
                    {days.map((d) => (
                      <th
                        key={d.key}
                        className={`min-w-[86px] px-1 pb-2 text-center text-xs font-medium uppercase tracking-wide ${
                          d.esFuturo ? 'text-muted-foreground/40' : 'text-muted-foreground'
                        }`}
                      >
                        {d.label}
                      </th>
                    ))}
                    <th className="min-w-[70px] px-2 pb-2 text-center text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Semana
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tareas.map((t) => {
                    const completa =
                      t.resumen.diasEvaluables > 0 &&
                      t.resumen.diasOk === t.resumen.diasEvaluables;
                    return (
                      <tr key={t.key}>
                        <td className="sticky left-0 z-10 bg-card px-2 py-1 align-middle">
                          <p className="font-medium leading-tight text-foreground">
                            {t.label}
                          </p>
                          <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
                            {t.descripcion}
                          </p>
                        </td>
                        {days.map((d) => (
                          <td key={d.key} className="px-0.5 py-1 align-middle">
                            <Celda celda={t.celdas[d.key]} tipo={t.tipo} />
                          </td>
                        ))}
                        <td className="px-2 py-1 text-center align-middle">
                          <span
                            className={`text-sm font-semibold tabular-nums ${
                              completa
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {t.resumen.diasOk}/{t.resumen.diasEvaluables}
                          </span>
                          {t.tipo === 'pendientes' && t.resumen.pendientesSemana > 0 && (
                            <p className="mt-0.5 text-[10px] leading-tight text-amber-600 dark:text-amber-400">
                              {t.resumen.pendientesSemana} por cerrar
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-border/50 pt-3">
              {Object.entries(ESTADOS).map(([key, meta]) => (
                <span key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={`h-3 w-3 rounded border ${meta.className}`} />
                  {meta.label}
                </span>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default WeeklyChecklistCard;
